import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, InputNumber, Radio, Spin, message } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { A11Y_ROLES } from '@/utils/a11y-attrs';
import { roadTestApi } from '@/fetch/bussiness/roadtest/api';
import {
  ApiResponse,
  RoadTestScheme,
  RoadTestTaskType,
} from '@/fetch/bussiness/roadtest/types';
import { HttpStatusCode } from '@/fetch/core/constant';
import SchemeInfo from './components/SchemeInfo';

export interface CreateTaskDrawerProps {
  vehicleName: string;
  onCreated: (taskType: RoadTestTaskType) => void;
  /** 抽屉是否展开，展开时才加载方案列表 */
  open?: boolean;
}

export const CREATE_TASK_DRAWER_TEST_ID = 'create-task-drawer';

/**
 * 路测任务创建抽屉
 *
 * @param {*} {
 *   vehicleName,
 *   onCreated,
 *   open = true,
 * }
 * @return {*}
 */
const CreateTaskDrawer: React.FC<CreateTaskDrawerProps> = ({
  vehicleName,
  onCreated,
  open = true,
}) => {
  // 路测类型(综合/疲劳)
  const [taskType, setTaskType] = useState<RoadTestTaskType>(
    RoadTestTaskType.COMPREHENSIVE
  );
  // 疲劳类型的跑行趟次
  const [fatigueLaps, setFatigueLaps] = useState<number | null>(null);
  // 路测方案列表
  const [schemeList, setSchemeList] = useState<RoadTestScheme[]>([]);
  // 选中的路测方案号
  const [selectedSchemeNo, setSelectedSchemeNo] = useState<string>();
  // 路测方案加载状态
  const [loading, setLoading] = useState(false);
  // 路测任务创建提交状态
  const [submitting, setSubmitting] = useState(false);

  /**
   * 获取路测方案列表
   */
  const fetchSchemeList = useCallback(
    async (type: RoadTestTaskType) => {
      setLoading(true);
      try {
        const res: ApiResponse<{ schemeList: RoadTestScheme[] }> =
          await roadTestApi.getSchemeList({
            taskType: type,
            vehicleName,
          });
        if (res?.code === HttpStatusCode.Success) {
          setSchemeList(res.data?.schemeList || []);
        } else {
          message.error(res?.message || '获取路测方案失败');
          setSchemeList([]);
        }
      } catch (e) {
        message.error('获取路测方案失败');
        setSchemeList([]);
      } finally {
        setLoading(false);
      }
    },
    [vehicleName]
  );

  /**
   * 页面挂载--获取路测方案列表
   */
  useEffect(() => {
    if (!open) return;
    fetchSchemeList(taskType);
  }, [taskType, fetchSchemeList, open]);

  /**
   * 路测类型切换回调
   * 1. 切换路测类型
   * 2. 清空选中方案
   * 3. 如果切换到综合类型，清空疲劳趟次
   */
  const handleTaskTypeChange = useCallback((e: RadioChangeEvent) => {
    const nextType = e.target.value as RoadTestTaskType;
    setTaskType(nextType);
    setSelectedSchemeNo(undefined);
    if (nextType !== RoadTestTaskType.FATIGUE) {
      setFatigueLaps(null);
    }
  }, []);

  /**
   * 疲劳测试跑行趟次输入回调
   */
  const handleFatigueLapsChange = useCallback((val: number | null) => {
    setFatigueLaps(val);
  }, []);

  /**
   * 选择路测方案回调
   */
  const handleSchemeChange = useCallback((schemeNo: string) => {
    setSelectedSchemeNo(schemeNo);
  }, []);

  /**
   * 选中的路测方案对象
   */
  const selectedScheme = useMemo(() => {
    return schemeList.find((s) => s.schemeNo === selectedSchemeNo);
  }, [schemeList, selectedSchemeNo]);

  /**
   * 创建路测任务回调
   */
  const handleCreate = useCallback(async () => {
    if (!selectedSchemeNo) {
      message.warning('请选择跑行线路');
      return;
    }
    if (
      taskType === RoadTestTaskType.FATIGUE &&
      (typeof fatigueLaps !== 'number' || fatigueLaps < 1 || fatigueLaps > 100)
    ) {
      message.warning('请填写跑行趟次');
      return;
    }
    const params: {
      taskType: RoadTestTaskType;
      vehicleName: string;
      taskSchemeNo: string;
      fatigueLaps?: number;
    } = {
      taskType,
      vehicleName,
      taskSchemeNo: selectedSchemeNo,
    };
    if (
      taskType === RoadTestTaskType.FATIGUE &&
      typeof fatigueLaps === 'number'
    ) {
      params.fatigueLaps = fatigueLaps;
    }
    setSubmitting(true);
    try {
      const res: ApiResponse = await roadTestApi.createTask(params);
      if (res?.code === HttpStatusCode.Success) {
        message.success('创建成功');
        onCreated(taskType);
      } else {
        message.error(res?.message || '创建失败');
      }
    } catch (e) {
      message.error('创建失败');
    } finally {
      setSubmitting(false);
    }
  }, [selectedSchemeNo, taskType, vehicleName, fatigueLaps, onCreated]);

  /**
   * 打开所有路测任务回调
   */
  const handleOpenAllTasks = useCallback(() => {
    const url = `${location.protocol}//${process.env.JDX_APP_OPERATION_PLATFORM}/app/roadTestEvaluation?vehicleName=${encodeURIComponent(
      vehicleName
    )}`;
    window.open(url, '_blank');
  }, [vehicleName]);

  return (
    <div
      className="create-task-drawer"
      data-testid={CREATE_TASK_DRAWER_TEST_ID}
    >
      <Spin spinning={loading}>
        {/* 可滚动内容区域 */}
        <div className="create-task-content">
          {/* 路测类型 */}
          <div className="drawer-section task-type-section">
            <div className="section-label">路测类型：</div>
            <Radio.Group
              value={taskType}
              onChange={handleTaskTypeChange}
              data-testid="create-task-type"
              {...A11Y_ROLES.radio('路测类型选择')}
            >
              <Radio
                value={RoadTestTaskType.COMPREHENSIVE}
                {...A11Y_ROLES.radio(
                  '综合',
                  taskType === RoadTestTaskType.COMPREHENSIVE
                )}
              >
                综合
              </Radio>
              <Radio
                value={RoadTestTaskType.FATIGUE}
                {...A11Y_ROLES.radio(
                  '疲劳',
                  taskType === RoadTestTaskType.FATIGUE
                )}
              >
                疲劳
              </Radio>
            </Radio.Group>
          </div>

          {/* 疲劳类型：跑行趟次 */}
          {taskType === RoadTestTaskType.FATIGUE && (
            <div className="drawer-section fatigue-laps-section">
              <div className="section-label">跑行趟次：</div>
              <InputNumber
                min={1}
                max={100}
                value={fatigueLaps}
                onChange={handleFatigueLapsChange}
                placeholder="请输入跑行趟次"
                data-testid="create-task-fatigue-laps"
              />
            </div>
          )}

          {/* 选择跑行线路 */}
          <div className="drawer-section scheme-table-section">
            <div className="scheme-section-title">选择跑行线路</div>
            <div className="scheme-table">
              <div className="scheme-table-header">
                <span className="scheme-col-radio">单选</span>
                <span className="scheme-col-name">方案名称</span>
              </div>
              <div className="scheme-table-body">
                {schemeList.map((scheme) => (
                  <div
                    key={scheme.schemeNo}
                    {...A11Y_ROLES.radio(
                      scheme.schemeName,
                      selectedSchemeNo === scheme.schemeNo
                    )}
                    className={`scheme-table-row${selectedSchemeNo === scheme.schemeNo ? ' scheme-table-row-active' : ''}`}
                    onClick={() => handleSchemeChange(scheme.schemeNo)}
                    data-testid={`scheme-row-${scheme.schemeNo}`}
                  >
                    <span className="scheme-col-radio">
                      <Radio checked={selectedSchemeNo === scheme.schemeNo} />
                    </span>
                    <span className="scheme-col-name">{scheme.schemeName}</span>
                  </div>
                ))}
                {schemeList.length === 0 && !loading && (
                  <div className="scheme-empty-text">暂无可用方案</div>
                )}
              </div>
            </div>
          </div>

          {/* 选中方案的详细信息（测试项 + 途径点位） */}
          {selectedScheme && <SchemeInfo scheme={selectedScheme} />}
        </div>
      </Spin>

      {/* 底部按钮 - fixed */}
      <div className="drawer-footer">
        <Button
          type="primary"
          loading={submitting}
          disabled={submitting}
          onClick={handleCreate}
          data-testid="create-task-submit-btn"
        >
          立即出发
        </Button>
        <Button
          type="link"
          onClick={handleOpenAllTasks}
          data-testid="create-task-all-tasks-link"
        >
          全部路测任务 &gt;&gt;
        </Button>
      </div>
    </div>
  );
};

export default React.memo(CreateTaskDrawer);
